<template>
   <v-card>
      <v-card-title>New Membership Application</v-card-title>
      <v-card-subtitle>
         New user detected. You will have to complete the Membership Application process in order to fully experience
         MIP Online Membership System. Take your time to complete the required information. You can logout and login back
         to continue the registration process at anytime.
      </v-card-subtitle>
      <v-stepper v-model="currstep">
         <v-stepper-header>
            <v-stepper-step :complete="validstep1" step="1">
               <div class="text-sm">Category</div>
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="validstep2" step="2">
               <div class="text-sm">Details</div>
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="validstep3" step="3">
               <div class="text-sm">Qualifications</div>
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="(modelstep1.membertype !== 'STU') ? (currstep > 4) : false" step="4">
               <div class="text-sm">Employment</div>
            </v-stepper-step>
            <v-stepper-step :complete="(modelstep1.membertype === 'COR') ? (currstep > 5) : false" step="5">
               <div class="text-sm">Corporate Experience</div>
            </v-stepper-step>
            <v-divider></v-divider>
            <v-stepper-step :complete="validstep6" step="6">
               <div class="text-sm">Declaration</div>
            </v-stepper-step>
            <v-divider></v-divider>
         </v-stepper-header>

         <v-stepper-content step="1">
            <v-simple-table class="mb-7 pa-3 cyan lighten-1" style="border-radius: 10px;" dense dark>
               <template v-slot:default>
                  <thead>
                     <tr>
                        <th colspan="2" class="text-left">
                           <h4 class="text-white">Application Procedures</h4>
                        </th>
                     </tr>
                  </thead>
                  <tbody>
                     <tr
                        v-for="item in selectedprocs"
                        :key="item.id"
                     >
                        <td class="align-top">{{ item.id }}</td>
                        <td class="align-top">{{ item.desc }}</td>
                     </tr>
                  </tbody>
               </template>
            </v-simple-table>
            <v-card class="mb-12">
               <v-form v-model="validstep1">
                  <v-jsf v-model="modelstep1" :schema="schemastep1" :options="options"/>
               </v-form>
            </v-card>
            <v-btn color="primary" @click="updatecategory" :disabled="submitting1 || !validstep1">
               Next
            </v-btn>
         </v-stepper-content>

         <v-stepper-content step="2">
            <v-card class="mb-12">
               <v-row>
                  <v-col cols="9">
                     <v-form v-model="validstep2">
                        <v-jsf v-model="modelstep2" :schema="schemastep2" :options="options"/>
                     </v-form>
                  </v-col>
                  <v-col cols="3" class="text-center">
                     <v-avatar size="180">
                        <v-img
                           :src="previewphoto"
                           :alt="modelstep2.sectdetails.fullname"
                        />
                     </v-avatar>
                     <v-file-input
                        ref="photofile"
                        prepend-icon="mdi-camera"
                        show-size
                        v-model="photofile"
                        accept="image/png, image/jpeg, image/bmp"
                        placeholder="Pick a photo"
                        :error-messages="photoValidationState === 'error' ? [photoValidationMessage] : []"
                        :loading="photoValidating"
                        hint="Professional photo required. JPG, PNG, BMP up to 1MB."
                        persistent-hint
                        @change="validateSelectedPhoto"
                     />
                     <div class="caption grey--text mt-1">
                        JPG/PNG/BMP, max 1MB. Professional face photo only.
                     </div>
                     <div class="caption mt-2" :class="photoValidationClass" v-if="photoValidationMessage">
                        {{ photoValidationMessage }}
                     </div>
                     <div class="caption red--text mt-2" v-if="!previewphoto">
                        Professional photo is required.
                     </div>
                     <v-alert v-if="detailsError" dense outlined type="error" class="mt-3 text-left">
                        {{ detailsError }}
                     </v-alert>
                  </v-col>
               </v-row>
            </v-card>
            <v-btn color="primary" @click="updatedetails" :disabled="submitting2 || photoValidating || photoValidationState === 'error' || !validstep2">
               Next
            </v-btn>
            <v-btn @click="currstep = 1">
               Back
            </v-btn>
         </v-stepper-content>

         <v-stepper-content step="3">
            <v-card class="mb-12">
               <v-alert v-if="qualificationError" dense outlined type="error" class="mb-4">
                  {{ qualificationError }}
               </v-alert>
               <v-list-item-subtitle class="text-wrap">
                  Clearly list all of your relevant qualification below
               </v-list-item-subtitle>
               <table-qual-edu ref="qualedu"></table-qual-edu>
               <v-divider v-if="modelstep1.membertype !== 'STU'"></v-divider>
               <table-qual-pro ref="qualpro" v-if="modelstep1.membertype !== 'STU'"></table-qual-pro>
               <v-divider v-if="modelstep1.membertype === 'STU'"></v-divider>
               <table-qual-stu ref="qualstu" v-if="modelstep1.membertype === 'STU'"></table-qual-stu>
            </v-card>
            <v-btn color="primary" @click="goNextFromQualifications">
               Next
            </v-btn>
            <v-btn @click="currstep = 2">
               Back
            </v-btn>
         </v-stepper-content>

         <v-stepper-content step="4" v-if="modelstep1.membertype !== 'STU'">
            <v-card class="mb-12">
               <v-card-text>
                  <p>Please begin with summary job, list only professional employment</p>
                  <table-emp-exp ref="empexp"></table-emp-exp>
                  <v-divider></v-divider>
                  <table-emp-cat ref="empcat" v-on:EmpCatUpdated="onEmpCatUpdated"></table-emp-cat>
               </v-card-text>
            </v-card>
            <v-btn color="primary" @click="updateempcat">
               Next
            </v-btn>
            <v-btn @click="currstep = 3">
               Back
            </v-btn>
         </v-stepper-content>

         <v-stepper-content step="5" v-if="modelstep1.membertype === 'COR'">
            <v-card class="mb-12">
               <table-corp-exp ref="corpexp" v-on:CorpExpCompleted="onCorpExpCompleted"></table-corp-exp>
            </v-card>
            <v-btn color="primary" @click="currstep = 6" :disabled="!validstep5">
               Next
            </v-btn>
            <v-btn @click="currstep = 4">
               Back
            </v-btn>
         </v-stepper-content>

         <v-stepper-content step="6">
            <v-card class="mb-12">
               <v-alert v-if="submitError" type="error" dense outlined class="mb-4">
                  {{ submitError }}
               </v-alert>
               <table-decl ref="decl" v-on:accepted="onDeclarationAccepted" v-on:declupdated="onDeclUpdated"></table-decl>
            </v-card>
            <v-btn color="primary" @click="updatedecl" :disabled="submitting6 || !validstep6">
               Submit
            </v-btn>
            <v-btn @click="moveback(6)">
               Back
            </v-btn>
         </v-stepper-content>

      </v-stepper>
      <v-card-text>
         testing area
      </v-card-text>
   </v-card>
</template>

<script>
import axios from 'axios'
import VJsf from '@koumoul/vjsf/lib/VJsf.js'
import '@koumoul/vjsf/lib/VJsf.css'
import '@koumoul/vjsf/lib/deps/third-party.js'
import GeneralOptions from '../Forms/GeneralOptions.js'
import CategoryModel from '../Forms/CategoryModel.js'
import CategorySchema from '../Forms/CategorySchema.js'
import PersonalDetailsModel from '../Forms/PersonalDetailsModel.js'
import PersonalDetailsSchema from '../Forms/PersonalDetailsSchema.js'
import aprocs from '../Forms/ApplProcs.js'
import TableQualEdu from '../Tables/TableQualEdu.vue'
import TableQualPro from '../Tables/TableQualPro.vue'
import TableQualStu from '../Tables/TableQualStu.vue'
import TableEmpExp from '../Tables/TableEmpExp.vue'
import TableEmpCat from '../Tables/TableEmpCat.vue'
import TableCorpExp from '../Tables/TableCorpExp.vue'
import TableDecl from '../Tables/TableDecl.vue'
import { validateProfessionalPhoto } from '../../utils/professionalPhotoValidator.js'
// import Payment from './Tables/Payment.vue'

export default {
   name: "PanelNewApplication",
   props: {},
   components: {
      TableQualEdu,
      TableQualPro,
      TableQualStu,
      TableEmpExp,
      TableEmpCat,
      TableCorpExp,
      TableDecl,
      VJsf,
   },
   data() { return {
      user: null,
      memberinfo: null,
      currstep: 1,
      options: GeneralOptions,
      validstep1: false,
      modelstep1: CategoryModel,
      schemastep1: CategorySchema,
      submitting1: false,
      validstep2: false,
      modelstep2: PersonalDetailsModel,
      schemastep2: PersonalDetailsSchema,
      submitting2: false,
      validstep3: false,
      qualificationError: '',
      submitting5: false,
      validstep5: false,
      submitting6: false,
      validstep6: false,
      photofile: null,
      previewphoto: null,
      photoValidationMessage: '',
      photoValidationState: '',
      photoValidating: false,
      photoResetting: false,
      selectedprocs: null,
      detailsLoaded: false,
      autosaveTimer: null,
      autosaving: false,
      photoError: '',
      detailsError: '',
      submitError: '',
      selectedprocs: null,
      modelsLoaded: false,
      isValidatingPhoto: false,
   }},

   methods: {
      async validateSelectedPhoto(file) {
         this.photoValidationMessage = ''
         this.photoValidationState = ''

         if (!file) {
            return true
         }

         this.previewphoto = URL.createObjectURL(file)
         this.photoValidating = true
         this.photoValidationMessage = 'Checking professional photo...'
         this.photoValidationState = 'info'

         try {
            const result = await validateProfessionalPhoto(file)
            if (!result.valid) {
               this.photoValidationMessage = result.message
               this.photoValidationState = 'error'
               this.photoResetting = true
               this.photofile = null
               this.previewphoto = null
               return false
            }

            this.photoValidationMessage = result.message
            this.photoValidationState = 'success'
            return true
         } catch (error) {
            this.photoValidationMessage = error.message || 'Unable to validate photo.'
            this.photoValidationState = 'error'
            this.photoResetting = true
            this.photofile = null
            this.previewphoto = null
            return false
         } finally {
            this.photoValidating = false
         }
      },

      onDeclarationAccepted(arg) {
         if (arg === 'OK') {
            this.validstep6 = true
         } else {
            this.validstep6 = false
         }
      },

      onDeclUpdated() {
         this.submitapplication()
      },

      onCorpExpCompleted() {
         this.validstep5 = true
      },

      qualificationItemCount(refName) {
         const ref = this.$refs[refName]
         if (!ref || !Array.isArray(ref.listitems)) {
            return 0
         }
         return ref.listitems.length
      },

      validateQualifications() {
         const totalItems =
            this.qualificationItemCount('qualedu') +
            this.qualificationItemCount('qualpro') +
            this.qualificationItemCount('qualstu')

         this.validstep3 = totalItems > 0
         this.qualificationError = this.validstep3
            ? ''
            : 'Please add at least one qualification record before continuing.'

         return this.validstep3
      },

      goNextFromQualifications() {
         if (!this.validateQualifications()) {
            return
         }

         this.currstep = (this.modelstep1.membertype !== 'STU') ? 4 : 6
      },

      onEmpCatUpdated() {
         if (this.modelstep1.membertype === 'COR') {
            this.currstep = 5
         } else {
            this.currstep = 6
         }
      },

      initialize() {
         this.$nextTick( function () {
            this.user = this.$page.user
            this.getmemberinfo()
            if (this.$refs.qualedu) {
               this.$refs.qualedu.setuserid(this.user.id)
            }
            if (this.$refs.qualpro) {
               this.$refs.qualpro.setuserid(this.user.id)
            }
            if (this.$refs.qualstu) {
               this.$refs.qualstu.setuserid(this.user.id)
            }
            if (this.$refs.empexp) {
               this.$refs.empexp.setuserid(this.user.id)
            }
            if (this.$refs.empcat) {
               this.$refs.empcat.setuserid(this.user.id)
            }
            if (this.$refs.corpexp) {
               this.$refs.corpexp.setuserid(this.user.id)
            }
            if (this.$refs.decl) {
               this.$refs.decl.setuserid(this.user.id)
            }
         })
      },

      getmemberinfo() {
         var url = '/member/info/' + this.user.id
         axios.get(url)
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  this.memberinfo = rtn.data
               } else {
                  console.log(rtn.status + ': ' + rtn.error)
               }
            }
         })
      },

      submitapplication() {
         this.submitting6 = true
         this.submitError = ''
         var targetPath = '/member/apply/submit'
         var data = {
            userid: this.user.id
         }
         axios.post(targetPath, data)
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  this.submitting6 = false
                  window.location.href = "/dashboard" // reloads dashboard
               } else {
                  this.submitting6 = false
                  this.submitError = rtn.error || rtn.message || 'Unable to submit application. Please check your information and try again.'
               }
            }
         })
         .catch(() => {
            this.submitting6 = false
            this.submitError = 'Unable to submit application. Please check your information and try again.'
         })
      },

      updatedecl() {
         this.submitError = ''
         this.$refs.decl.update()
      },

      updateempcat() {
         this.$refs.empcat.update()
      },

      updatecategory() {
         var targetPath = "/member/apply/updatecategory"
         var data = {
            userid: this.user.id,
            membertype: this.modelstep1.membertype
         }
         this.submitting1 = true
         axios.post(targetPath, data)
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  this.submitting1 = false
                  this.initialize()
                  this.currstep = 2
               } else {
                  console.log(rtn.status + ': ' + rtn.error)
               }
            }
         })
      },

      appendStep2Details(data) {
         data.append('userid', this.user.id)
         data.append('membertype', this.modelstep1.membertype)
         data.append('fullname', this.modelstep2.sectdetails.fullname)
         data.append('gender', this.modelstep2.sectdetails.gender)
         data.append('nationality', this.modelstep2.sectdetails.nationality)
         data.append('race', this.modelstep2.sectdetails.race)
         data.append('religion', this.modelstep2.sectdetails.religion)
         data.append('icpassport', this.modelstep2.sectdetails.icpassport)
         data.append('dob', this.modelstep2.sectdetails.dob)
         data.append('pob', this.modelstep2.sectdetails.pob)
         data.append('contactmobile', this.modelstep2.sectdetails.contactmobile)
         data.append('contactemail', this.modelstep2.sectdetails.contactemail)
         data.append('homestreet', this.modelstep2.secthome.homestreet)
         data.append('homepostcode', this.modelstep2.secthome.homepostcode)
         data.append('homecity', this.modelstep2.secthome.homecity)
         data.append('homedistrict', this.modelstep2.secthome.homedistrict)
         data.append('homestate', this.modelstep2.secthome.homestate)
         data.append('homecountry', this.modelstep2.secthome.homecountry)
         data.append('homephone', this.modelstep2.secthome.homephone)
         data.append('workcompany', this.modelstep2.sectwork.workcompany)
         data.append('workjobtitle', this.modelstep2.sectwork.workjobtitle)
         data.append('workdept', this.modelstep2.sectwork.workdept)
         data.append('workposition', this.modelstep2.sectwork.workposition)
         data.append('workstreet', this.modelstep2.sectwork.workstreet)
         data.append('workpostcode', this.modelstep2.sectwork.workpostcode)
         data.append('workcity', this.modelstep2.sectwork.workcity)
         data.append('workdistrict', this.modelstep2.sectwork.workdistrict)
         data.append('workstate', this.modelstep2.sectwork.workstate)
         data.append('workcountry', this.modelstep2.sectwork.workcountry)
         data.append('workphone', this.modelstep2.sectwork.workphone)
         data.append('workfax', this.modelstep2.sectwork.workfax)
         data.append('workemail', this.modelstep2.sectwork.workemail)
         data.append('prefercontacthome', this.modelstep2.sectcontactpref.prefercontacthome)
         data.append('prefercontactwork', this.modelstep2.sectcontactpref.prefercontactwork)
         data.append('prefercontactemail', this.modelstep2.sectcontactpref.prefercontactemail)
      },

      scheduleAutosave() {
         if (!this.detailsLoaded || !this.user || this.currstep !== 2) {
            return
         }
         clearTimeout(this.autosaveTimer)
         this.autosaveTimer = setTimeout(() => {
            this.autosavedetails()
         }, 1200)
      },

      autosavedetails() {
         if (this.autosaving) {
            return
         }

         const data = new FormData()
         this.appendStep2Details(data)
         this.autosaving = true
         axios.post('/member/apply/autosavedetails', data)
         .then((resp) => {
            if (resp.data && resp.data.status === 'ERR') {
               this.detailsError = resp.data.error || 'Unable to auto-save membership details'
            }
         })
         .finally(() => {
            this.autosaving = false
         })
      },

      applyDobFromIc(value, force = false) {
         const dob = this.dobFromMalaysianIc(value)
         if (!dob) {
            return
         }

         if (force || !this.modelstep2.sectdetails.dob) {
            this.modelstep2.sectdetails.dob = dob
         }
         this.modelstep2.sectdetails.age = this.calculateAge(dob)
      },

      dobFromMalaysianIc(value) {
         const digits = String(value || '').replace(/\D/g, '')
         if (digits.length < 6) {
            return null
         }

         const yy = parseInt(digits.substring(0, 2), 10)
         const mm = parseInt(digits.substring(2, 4), 10)
         const dd = parseInt(digits.substring(4, 6), 10)
         const currentYear = new Date().getFullYear()
         const currentYY = currentYear % 100
         const year = yy <= currentYY ? 2000 + yy : 1900 + yy
         const date = new Date(year, mm - 1, dd)

         if (date.getFullYear() !== year || date.getMonth() !== mm - 1 || date.getDate() !== dd) {
            return null
         }

         return `${year}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
      },

      calculateAge(dateString) {
         const today = new Date()
         const birthDate = new Date(dateString)
         let age = today.getFullYear() - birthDate.getFullYear()
         const monthDiff = today.getMonth() - birthDate.getMonth()

         if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--
         }

         return age
      },

      moveback(e) {
         if (e === 6) {
            if (this.modelstep1.membertype === 'COR') {
               this.currstep = 5
               return
            }
            if (this.modelstep1.membertype === 'STU') {
               this.currstep = 3
               return
            }
            if ((this.modelstep1.membertype !== 'COR') || (this.modelstep1.membertype !== 'STU')) {
               this.currstep = 4
               return
            }
         }
      },

      async updatedetails() {
         this.detailsError = ''

         if (this.$refs.detailsform) {
            this.$refs.detailsform.validate()
         }

         if (!this.photofile && !this.previewphoto) {
            this.detailsError = 'Professional photo is required.'
            return
         }

         if (this.photofile) {
            const photoOk = await this.validateSelectedPhoto(this.photofile)
            if (!photoOk) {
               return
            }
         }

         var targetPath = "/member/apply/updatedetails"

         var data = new FormData()
         data.append('userid', this.user.id)
         data.append('fullname', this.modelstep2.sectdetails.fullname)
         data.append('gender', this.modelstep2.sectdetails.gender)
         data.append('nationality', this.modelstep2.sectdetails.nationality)
         data.append('race', this.modelstep2.sectdetails.race)
         data.append('religion', this.modelstep2.sectdetails.religion)
         data.append('icpassport', this.modelstep2.sectdetails.icpassport)
         data.append('dob', this.modelstep2.sectdetails.dob)
         data.append('pob', this.modelstep2.sectdetails.pob)
         data.append('contactmobile', this.modelstep2.sectdetails.contactmobile)
         data.append('contactemail', this.modelstep2.sectdetails.contactemail)
         data.append('homestreet', this.modelstep2.secthome.homestreet)
         data.append('homepostcode', this.modelstep2.secthome.homepostcode)
         data.append('homecity', this.modelstep2.secthome.homecity)
         data.append('homedistrict', this.modelstep2.secthome.homedistrict)
         data.append('homestate', this.modelstep2.secthome.homestate)
         data.append('homecountry', this.modelstep2.secthome.homecountry)
         data.append('homephone', this.modelstep2.secthome.homephone)
         data.append('workcompany', this.modelstep2.sectwork.workcompany)
         data.append('workjobtitle', this.modelstep2.sectwork.workjobtitle)
         data.append('workdept', this.modelstep2.sectwork.workdept)
         data.append('workposition', this.modelstep2.sectwork.workposition)
         data.append('workstreet', this.modelstep2.sectwork.workstreet)
         data.append('workpostcode', this.modelstep2.sectwork.workpostcode)
         data.append('workcity', this.modelstep2.sectwork.workcity)
         data.append('workdistrict', this.modelstep2.sectwork.workdistrict)
         data.append('workstate', this.modelstep2.sectwork.workstate)
         data.append('workcountry', this.modelstep2.sectwork.workcountry)
         data.append('workphone', this.modelstep2.sectwork.workphone)
         data.append('workfax', this.modelstep2.sectwork.workfax)
         data.append('workemail', this.modelstep2.sectwork.workemail)
         data.append('prefercontacthome', this.modelstep2.sectcontactpref.prefercontacthome)
         data.append('prefercontactwork', this.modelstep2.sectcontactpref.prefercontactwork)
         data.append('prefercontactemail', this.modelstep2.sectcontactpref.prefercontactemail)
         if (this.photofile) {
            data.append('photolink', this.photofile)
         }

         this.submitting2 = true
         axios.post(targetPath, data, {
            headers: {
               'Content-Type': 'multipart/form-data'
            }
         })
         .then((resp) => {
            if (resp.data) {
               var rtn = resp.data
               if (rtn.status === 'OK') {
                  this.submitting2 = false
                  this.detailsError = ''
                  if (rtn.profile_photo_url && this.$page && this.$page.user) {
                     this.$page.user.profile_photo_url = rtn.profile_photo_url
                  }
                  if (rtn.data && rtn.data.photolink) {
                     this.previewphoto = '/' + String(rtn.data.photolink).replace(/^\/+/, '')
                  }
                  this.getmemberinfo()
                  this.currstep = 3
               } else {
                  this.submitting2 = false
                  this.detailsError = rtn.error
                  console.log(rtn.status + ': ' + rtn.error)
               }
            }
         })
         .catch((error) => {
            this.submitting2 = false
            this.detailsError = error.response?.data?.error || error.response?.data?.message || 'Unable to update membership details'
         })
      },

      loadcategory() {
         this.modelstep1.membertype = this.memberinfo.membertype
      },

      loadprocedures(e) {
         var result = aprocs.ApplProcs.find(obj => {
            return obj.membertype === e
         })
         if (result) {
            this.selectedprocs = result.proc
         }
      },

      loaddetails() {
         this.detailsLoaded = false
         const signupName = (this.user && this.user.name) ? this.user.name : ''
         const signupIc = (this.user && this.user.icpassport) ? this.user.icpassport : ''
         const signupEmail = (this.user && this.user.email) ? this.user.email : ''

         // sectdetails
         if (this.memberinfo.fullname) {
            this.modelstep2.sectdetails.fullname = this.memberinfo.fullname || signupName
            this.modelstep2.sectdetails.gender = this.memberinfo.gender
            this.modelstep2.sectdetails.nationality = this.memberinfo.nationality
            this.modelstep2.sectdetails.race = this.memberinfo.race
            this.modelstep2.sectdetails.religion = this.memberinfo.religion
            this.modelstep2.sectdetails.icpassport = this.memberinfo.icpassport || signupIc
            this.modelstep2.sectdetails.dob = this.memberinfo.dob
            this.modelstep2.sectdetails.pob = this.memberinfo.pob
            this.modelstep2.sectdetails.contactmobile = this.memberinfo.contactmobile
            this.modelstep2.sectdetails.contactemail = this.memberinfo.contactemail || signupEmail
            if (this.memberinfo.photolink) {
               this.previewphoto = "/" + this.memberinfo.photolink
            }
         } else {
            this.modelstep2.sectdetails.fullname = signupName
            this.modelstep2.sectdetails.icpassport = signupIc
            this.modelstep2.sectdetails.contactemail = signupEmail
         }
         this.applyDobFromIc(this.modelstep2.sectdetails.icpassport)

         // secthome
         if (this.memberinfo.homestreet) {
            this.modelstep2.secthome.homestreet = this.memberinfo.homestreet
            this.modelstep2.secthome.homepostcode = this.memberinfo.homepostcode
            this.modelstep2.secthome.homecity = this.memberinfo.homecity
            this.modelstep2.secthome.homedistrict = this.memberinfo.homedistrict
            this.modelstep2.secthome.homestate = this.memberinfo.homestate
            this.modelstep2.secthome.homecountry = this.memberinfo.homecountry
            this.modelstep2.secthome.homephone = this.memberinfo.homephone
         }
         // sectwork
         if (this.memberinfo.workcompany) {
            this.modelstep2.sectwork.workcompany = this.memberinfo.workcompany
            this.modelstep2.sectwork.workjobtitle = this.memberinfo.workjobtitle
            this.modelstep2.sectwork.workdept = this.memberinfo.workdept
            this.modelstep2.sectwork.workposition = this.memberinfo.workposition
            this.modelstep2.sectwork.workstreet = this.memberinfo.workstreet
            this.modelstep2.sectwork.workpostcode = this.memberinfo.workpostcode
            this.modelstep2.sectwork.workcity = this.memberinfo.workcity
            this.modelstep2.sectwork.workdistrict = this.memberinfo.workdistrict
            this.modelstep2.sectwork.workstate = this.memberinfo.workstate
            this.modelstep2.sectwork.workcountry = this.memberinfo.workcountry
            this.modelstep2.sectwork.workphone = this.memberinfo.workphone
            this.modelstep2.sectwork.workfax = this.memberinfo.workfax
            this.modelstep2.sectwork.workemail = this.memberinfo.workemail
         }
         // sectcontactpref
         this.modelstep2.sectcontactpref.prefercontacthome = this.memberinfo.prefercontacthome
         this.modelstep2.sectcontactpref.prefercontactwork = this.memberinfo.prefercontactwork
         this.modelstep2.sectcontactpref.prefercontactemail = this.memberinfo.prefercontactemail
         this.$nextTick(() => {
            this.detailsLoaded = true
         })
      },

   },
   watch: {
      photofile: function (newval, oldval) {
         if (this.photoResetting) {
            this.photoResetting = false
            return
         }

         if (!newval) {
            this.previewphoto = null
            this.photoValidationMessage = ''
            this.photoValidationState = ''
            return
         }

         this.validateSelectedPhoto(newval)
      },

      'modelstep1.membertype': function (newval, oldval){
         this.loadprocedures(newval)
      },

      'modelstep2.sectdetails.icpassport': function (newval) {
         this.applyDobFromIc(newval, true)
      },

      memberinfo: function (newval, oldval) {
         if (newval) {
            // --> 1. category
            this.loadcategory()
            // --> 2. details
            this.loaddetails()
         }
      },

      modelstep2: {
         deep: true,
         handler() {
            this.scheduleAutosave()
         }
      },
   },

   mounted() {
      this.initialize()
   },

   computed: {
      photoValidationClass() {
         if (this.photoValidationState === 'success') {
            return 'green--text'
         }
         if (this.photoValidationState === 'error') {
            return 'red--text'
         }
         return 'grey--text'
      },
   }
}
</script>
